import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { enqueueSnackbar } from "notistack";
import {
  getQueryIntent,
  getEntity,
  getQueryTabularData,
  chatWithAI,
  getChatMetaData,
  getPidResponse,
} from "../core/repo/chatRepo";
import { getAIModel } from "../core/storage/localStorage";
import { usePostHog } from "posthog-js/react";
import {
  STREAM_ANSWER_FAILURE,
  FETCH_METADATA_SUCCESS,
  FETCH_METADATA_FAILURE,
  GET_QUERY_INTENT_SUCCESS,
  STREAM_ANSWER_SUCCESS,
  GET_ENTITY_SUCCESS,
  GET_ENTITY_FAILURE,
} from "../utils/posthogEvents";
import { updateUsage } from "../utils/updateUsage";
import {
  updateSessionSuccess,
  setAnswerLoadingSuccess,
} from "../core/events/sessionEvents";
import { createSession, getRelatedDocs } from "../core/repo/sessionRepo";

export const useQueryHandler = (
  onMessageUpdate = () => {},
  sessionId = null
) => {
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const [tempQandA, setTempQandA] = useState(null);
  const [relatedDocs, setRelatedDocs] = useState([]);
  const [isMetadataLoading, setIsMetadataLoading] = useState(false);
  const { assistantMode, thinkingMode } = useSelector((state) => state.account);
  const { selectedDomain } = useSelector((state) => state.domain);
  const { sessions } = useSelector((state) => state.session);
  const model = getAIModel();

  const streamApiError = (err, sessId, queryId, query) => {
    let errMessage = "Could not connect to server. Please try again.";
    if (err["response"]) {
      const { status } = err["response"];
      switch (status) {
        case 500:
          errMessage = "Server Error";
          break;
        case 502:
          errMessage = "Service Unavailable";
          break;
      }
    }
    enqueueSnackbar(errMessage, { variant: "error" });
    posthog.capture(STREAM_ANSWER_FAILURE, {
      error: errMessage,
      query: query,
      messageId: queryId,
    });

    const { userQuery, aiResponse } = getDummyAnswerAndQuery(
      sessId,
      queryId,
      query,
      "",
      "stream_api_fail"
    );

    onMessageUpdate?.({ query: userQuery, answer: aiResponse });
    setTempQandA(null);
    dispatch(setAnswerLoadingSuccess(false));
    return "stream_api_error";
  };

  const handleEntity = async (
    sessId,
    queryId,
    query,
    queryIntent,
    taggedItems,
    threadId,
    domainId
  ) => {
    try {
      const entities = await dispatch(
        getEntity(
          queryIntent,
          query,
          sessId,
          queryId,
          threadId,
          true,
          taggedItems,
          model,
          domainId
        )
      );

      const { query_object, answer_object } = entities;
      onMessageUpdate?.({ query: query_object, answer: answer_object });
      updateUsage();
      posthog.capture(GET_ENTITY_SUCCESS);
    } catch (err) {
      posthog.capture(GET_ENTITY_FAILURE, {
        error: err.message,
        query: query,
        messageId: queryId,
      });
      streamApiError(err, sessId, queryId, query);
    }
    setTempQandA(null);
    dispatch(setAnswerLoadingSuccess(false));
  };

  const handleQueryTabularData = async (
    sessId,
    queryId,
    query,
    queryIntent,
    taggedItems,
    threadId,
    domainId
  ) => {
    try {
      const entities = await dispatch(
        getQueryTabularData(
          queryIntent,
          query,
          sessId,
          queryId,
          threadId,
          true,
          taggedItems,
          model,
          domainId
        )
      );

      const { query_object, answer_object } = entities;
      onMessageUpdate?.({ query: query_object, answer: answer_object });
      updateUsage();
    } catch (err) {
      streamApiError(err, sessId, queryId, query);
    }
    setTempQandA(null);
    dispatch(setAnswerLoadingSuccess(false));
  };

  const streamMessages = async (
    sessId,
    queryId,
    query,
    taggedItems,
    threadId,
    domainId
  ) => {
    let tempQueryResponse = "";
    try {
      await chatWithAI(
        query,
        sessId,
        queryId,
        threadId,
        true,
        taggedItems,
        model,
        domainId,
        assistantMode,
        thinkingMode,
        (text) => {
          if (text) {
            setTempQandA({
              query,
              queryId,
              response: text,
            });
            tempQueryResponse = text;
          }
        }
      );
      posthog.capture(STREAM_ANSWER_SUCCESS);
      updateUsage();
    } catch (err) {
      return streamApiError(err, sessId, queryId, query);
    }

    setIsMetadataLoading(true);
    try {
      const response = await getChatMetaData(queryId);
      const { ai_response, title, user_query } = response;
      const session = sessions.find((s) => s.id === sessId);
      if (
        session["title"] === "New Session" ||
        session["title"] === "~New Conversation"
      ) {
        dispatch(updateSessionSuccess({ ...session, title: title }));
      }
      onMessageUpdate?.({ query: user_query, answer: ai_response });
      posthog.capture(FETCH_METADATA_SUCCESS);
    } catch (e) {
      const { userQuery, aiResponse } = getDummyAnswerAndQuery(
        sessId,
        queryId,
        query,
        tempQueryResponse,
        "metadata_api_fail"
      );
      onMessageUpdate?.({ query: userQuery, answer: aiResponse });
      posthog.capture(FETCH_METADATA_FAILURE, {
        query: query,
        messageId: queryId,
        error: e.message,
      });
      console.log(e);
    }
    setIsMetadataLoading(false);
    setTempQandA(null);
    dispatch(setAnswerLoadingSuccess(false));
  };

  const handlePID = async (
    query,
    sessId,
    queryId,
    threadId,
    taggedItems,
    model,
    domainId
  ) => {
    try {
      const response = await dispatch(
        getPidResponse(
          {},
          query,
          sessId,
          queryId,
          threadId,
          true,
          taggedItems,
          model,
          domainId
        )
      );
      let { query_object, answer_object } = response;
      onMessageUpdate?.({ query: query_object, answer: answer_object });
      updateUsage();
    } catch (err) {
      streamApiError(err, sessId, queryId, query);
    }
    setTempQandA(null);
    dispatch(setAnswerLoadingSuccess(false));
  };

  function getChatAgent(intent) {
    if (intent === "document_request") {
      return "checklist_search";
    } else if (
      intent === "information_retrieval" ||
      intent === "procedural_query" ||
      intent === "open_ended_analysis" ||
      intent === "clarification_needed" ||
      intent === "out_of_scope" ||
      !intent
    ) {
      return "qna";
    }
  }

  const handleQueryIntent = async (
    query,
    taggedFiles = [],
    taggedCategories = []
  ) => {
    let queryId = uuidv4();
    let qna = {
      query: query,
      response: null,
      queryId: queryId,
      chatAgent: "qna",
    };
    let threadId = "";
    let sessId = sessionId;

    // Handle tagged files
    let taggedFileIds = [];

    taggedFiles.forEach((file) => {
      taggedFileIds.push(file.id);
    });

    // if (taggedFileIds.length === 0) {
    //   taggedFileIds = completedFiles.map((f) => f.id);
    // }

    let domainId = selectedDomain?.id || null;
    const taggedItems = {
      file_ids: taggedFileIds,
      categories: taggedCategories,
    };

    setTempQandA(qna);
    dispatch(setAnswerLoadingSuccess(true));
    try {
      const response = await dispatch(
        getRelatedDocs(query, selectedDomain.id, queryId, sessionId)
      );
      setRelatedDocs(response["chunks"] || []);
    } catch (err) {
      console.error(err);
    }

    try {
      // If chatAgent is pid_assist, skip query intent and go directly to handlePID
      // if (chatAgent === "pid_assist") {
      //   return handlePID(
      //     query,
      //     sessId,
      //     queryId,
      //     threadId,
      //     taggedItems,
      //     model,
      //     domainId
      //   );
      // }

      if (thinkingMode) {
        return streamMessages(
          sessId,
          queryId,
          query,
          taggedItems,
          threadId,
          domainId
        );
      } else {
        const intentResponse = await dispatch(
          getQueryIntent(
            query,
            sessId,
            queryId,
            threadId,
            true,
            taggedItems,
            model,
            domainId
          )
        );

        const { intent } = intentResponse;
        posthog.capture(GET_QUERY_INTENT_SUCCESS);
        setTempQandA({ ...qna, chatAgent: getChatAgent(intent) });

        if (intent === "document_request") {
          return handleEntity(
            sessId,
            queryId,
            query,
            intentResponse,
            taggedItems,
            threadId,
            domainId
          );
        } else {
          return streamMessages(
            sessId,
            queryId,
            query,
            taggedItems,
            threadId,
            domainId
          );
        }
      }
    } catch (err) {
      return streamApiError(err, sessId, queryId, query);
    }
  };

  return {
    handleQueryIntent,
    tempQandA,
    isMetadataLoading,
    relatedDocs,
  };
};

function getDummyAnswerAndQuery(
  sessionId,
  queryId,
  query,
  content,
  apiFailType
) {
  const userQuery = {
    content: query,
    content_object: { citations: [], file_ids: [], text: query },
    conversation_id: sessionId,
    thread_id: "",
    threads: [],
    role: "user",
    question_id: "",
    message_id: queryId,
    created_at: new Date().toISOString(),
  };
  const aiResponse = {
    content: content,
    content_object: {
      citations: [],
      file_ids: [],
      text: content,
    },
    conversation_id: sessionId,
    thread_id: "",
    threads: [],
    role: "assistant",
    question_id: queryId,
    message_id: uuidv4(),
    created_at: new Date().toDateString(),
    error: apiFailType,
  };
  return { userQuery, aiResponse };
}
